/**
 * Base Message component that branches rendering based on message role.
 * Routes to UserMessage, AssistantMessage, or SystemMessage components.
 */
import type { ChatMessage } from "@nucleus/shared-types";

import { AssistantMessage } from "./AssistantMessage";
import { UserMessage } from "./UserMessage";

interface MessageProps {
	/** The message to display */
	message: ChatMessage;
	/** Whether this is the first message in a group (affects avatar display) */
	isFirstInGroup?: boolean;
}

/**
 * Message component routes rendering to the appropriate sub-component based on role.
 * @param props - Component props
 * @param props.message - Message object containing role, content, timestamp, etc.
 * @param props.isFirstInGroup - Whether to show avatar (for message grouping)
 */
export function Message({ message, isFirstInGroup = true }: MessageProps) {
	switch (message.role) {
		case "user":
			return <UserMessage message={message} showAvatar={isFirstInGroup} />;

		case "assistant":
			return <AssistantMessage message={message} showAvatar={isFirstInGroup} />;

		case "system":
			return (
				<div className="flex justify-center py-2">
					<div className="rounded-md bg-muted px-4 py-2 text-sm text-muted-foreground">
						{message.content}
					</div>
				</div>
			);

		default:
			// Fallback for unknown role
			return (
				<div className="text-sm text-muted-foreground">
					Unknown message type: {message.role}
				</div>
			);
	}
}
